import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, StatCard } from '../components/ui/Card';
import { ChartCard } from '../components/ui/ChartCard';
import { Button } from '../components/ui/Button';
import { useDashboard } from '../hooks/useDashboard';
import { useAuth } from '../context/AuthContext';
import { Building2, Users, HardDrive, Activity, RefreshCw, ChevronRight, BarChart3, Download, TrendingUp, Clock, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, CartesianGrid, XAxis, YAxis } from 'recharts';

// Utility function to format time ago
function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInMs = now.getTime() - time.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} min ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  } else {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }
}

function generateActivityTrendData() {
  const now = new Date();
  const months: string[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(d.toLocaleString('en-US', { month: 'short' }));
  }
  return months.map((month) => ({
    month,
    devices: 0,
    users: 0,
    activities: 0,
  }));
}

// Generate mock customer distribution data
function generateCustomerDistribution(managedCustomers: any[]) {
  const tierCount: Record<string, number> = {};
  managedCustomers.forEach(customer => {
    const tier = customer.subscriptionTier || 'Standard';
    tierCount[tier] = (tierCount[tier] || 0) + 1;
  });

  const colors = {
    'Standard': '#94a3b8',
    'Professional': '#f97316',
    'Enterprise': '#10b981',
    'Premium': '#8b5cf6'
  };

  return Object.entries(tierCount).map(([tier, count]) => ({
    name: tier,
    value: count,
    color: colors[tier as keyof typeof colors] || '#64748b'
  }));
}

// Event type filter options
const EVENT_TYPES = ['All', 'Device', 'User', 'License', 'Security', 'Error'] as const;
type EventTypeFilter = typeof EVENT_TYPES[number];

export function Dashboard(): React.JSX.Element {
  const { 
    partner, 
    managedCustomers,
    error: authError
  } = useAuth();
  
  const { overview, recentActivity, isLoading, error: dashboardError, refreshData, clearError } = useDashboard();
  const navigate = useNavigate();
  
  const error = authError || dashboardError;

  // State for filters and pagination
  const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter>('All');
  const [activityLimit, setActivityLimit] = useState(10);
  const [sortField, setSortField] = useState<'name' | 'licenses' | 'status'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Generate mock chart data
  const activityTrendData = useMemo(() => generateActivityTrendData(), []);
  const customerDistribution = useMemo(() => generateCustomerDistribution(managedCustomers), [managedCustomers]);

  // GUID → friendly name lookup so the activity feed shows "Stratswen Group" instead of a raw GUID.
  const customerNameById = useMemo(() => {
    const map = new Map<string, string>();
    managedCustomers.forEach(c => {
      if (c.customerId) map.set(c.customerId, c.customerName || c.customerId);
    });
    return map;
  }, [managedCustomers]);

  // Generate sparkline data for stat cards (last 7 days trend)
  const sparklineData = useMemo(() => ({
    customers: [85, 87, 86, 89, 91, 90, managedCustomers.length],
    devices: [95, 98, 100, 105, 110, 115, overview?.totalDevices || 120],
    users: [130, 135, 138, 140, 142, 145, overview?.activeUsers || 150]
  }), [managedCustomers.length, overview]);

  // Filter activity by event type
  const filteredActivity = useMemo(() => {
    if (eventTypeFilter === 'All') return recentActivity;
    return recentActivity.filter(activity => {
      if (eventTypeFilter === 'Device') return activity.eventType.includes('Device');
      if (eventTypeFilter === 'User') return activity.eventType.includes('User');
      if (eventTypeFilter === 'License') return activity.eventType.includes('License');
      if (eventTypeFilter === 'Security') return activity.eventType.includes('Security');
      if (eventTypeFilter === 'Error') return activity.eventStatus === 'Error';
      return true;
    });
  }, [recentActivity, eventTypeFilter]);

  // Sort and prepare top customers
  const topCustomers = useMemo(() => {
    const sorted = [...managedCustomers].sort((a, b) => {
      if (sortField === 'name') {
        return sortDirection === 'asc' 
          ? a.customerName.localeCompare(b.customerName)
          : b.customerName.localeCompare(a.customerName);
      }
      if (sortField === 'licenses') {
        return sortDirection === 'asc'
          ? a.licenseCount - b.licenseCount
          : b.licenseCount - a.licenseCount;
      }
      if (sortField === 'status') {
        return sortDirection === 'asc'
          ? a.status.localeCompare(b.status)
          : b.status.localeCompare(a.status);
      }
      return 0;
    });
    return sorted.slice(0, 5);
  }, [managedCustomers, sortField, sortDirection]);

  // Handle export functionality
  const handleExport = () => {
    // TODO: Implement actual export logic
    // TODO: Implement actual export logic
  };

  // Get last sync time (mock for now)
  const lastSyncTime = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 p-6 bg-slate-50">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center space-y-4">
              <div className="relative">
                <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin mx-auto" />
              </div>
              <p className="text-sm text-slate-500 font-medium">Loading partner dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 p-6 bg-slate-50">
        <div className="max-w-[1800px] mx-auto">
          <Card className="border-rose-200 bg-rose-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-rose-100">
                  <svg className="h-5 w-5 text-rose-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-rose-800">Error loading dashboard</h3>
                  <p className="text-sm text-rose-700 mt-1">{error}</p>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={refreshData} variant="outline" size="sm">
                      Try Again
                    </Button>
                    <Button onClick={clearError} variant="ghost" size="sm">
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Enhanced Page Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex flex-col gap-4">
            {/* Header row */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Welcome back, {partner?.partnerName || 'Partner'}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Last synced: {lastSyncTime}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleExport} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button onClick={refreshData} variant="outline" size="sm" loading={isLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>

            {/* NOTE: Time range filter pills (Today / This Week / This Month) were removed
                because the underlying KPIs are point-in-time counts, not period-bounded — the
                pills toggled active state but never filtered the data. Re-add when historical
                trend data is wired through the API. */}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-[1800px] mx-auto space-y-6">

        {/* Enhanced Key Metrics Grid with Trends and Sparklines */}
        <div className="grid metrics-grid metrics-grid-4">
          <StatCard
            title="Managed Customers"
            value={managedCustomers.length}
            description="Total customer accounts"
            icon={<Building2 className="h-5 w-5 text-orange-600" />}
            color="orange"
            sparklineData={sparklineData.customers}
            loading={isLoading}
            onClick={() => navigate('/customers')}
          />
          <StatCard
            title="Active Customers"
            value={overview?.activeCustomers ?? managedCustomers.length}
            description="Currently active"
            icon={<Users className="h-5 w-5 text-emerald-600" />}
            color="emerald"
            loading={isLoading}
          />
          <StatCard
            title="Total Devices"
            value={overview?.totalDevices ?? 0}
            description="Across all customers"
            icon={<HardDrive className="h-5 w-5 text-blue-600" />}
            color="blue"
            sparklineData={sparklineData.devices}
            loading={isLoading}
          />
          <StatCard
            title="Active Users"
            value={overview?.activeUsers ?? 0}
            description="Monthly active"
            icon={<Activity className="h-5 w-5 text-amber-600" />}
            color="amber"
            sparklineData={sparklineData.users}
            loading={isLoading}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Trends Chart */}
          <ChartCard
            title="Activity Trends"
            description="Last 6 months overview"
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={activityTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="month" 
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="devices" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Devices"
                />
                <Line 
                  type="monotone" 
                  dataKey="users" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Users"
                />
                <Line 
                  type="monotone" 
                  dataKey="activities" 
                  stroke="#f97316" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Activities"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Customer Distribution Chart */}
          <ChartCard
            title="Customer Distribution"
            description="By subscription tier"
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={customerDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {customerDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Top Customers Table */}
        {managedCustomers.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle size="lg">Top Customers</CardTitle>
                  <CardDescription>Your highest priority accounts</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/customers')}>
                  View All
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setSortField('name');
                            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                          }}
                          className="flex items-center gap-1 hover:text-slate-900"
                        >
                          Customer
                          <TrendingUp className={`h-3 w-3 ${sortField === 'name' && sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                        </button>
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setSortField('status');
                            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                          }}
                          className="flex items-center gap-1 hover:text-slate-900"
                        >
                          Status
                          <TrendingUp className={`h-3 w-3 ${sortField === 'status' && sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                        </button>
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Tier</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setSortField('licenses');
                            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                          }}
                          className="flex items-center gap-1 ml-auto hover:text-slate-900"
                        >
                          Licenses
                          <TrendingUp className={`h-3 w-3 ${sortField === 'licenses' && sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                        </button>
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {topCustomers.map((customer) => (
                      <tr key={customer.customerId} className="hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center flex-shrink-0">
                              <Building2 className="h-4 w-4 text-orange-600" />
                            </div>
                            <div>
                              <div className="font-medium text-sm text-slate-900">{customer.customerName}</div>
                              <div className="text-xs text-slate-500">ID: {customer.customerId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge 
                            status={customer.status === 'Active' ? 'active' : 'inactive'} 
                            label={customer.status}
                          />
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={customer.subscriptionTier === 'Enterprise' ? 'success' : 'default'}>
                            {customer.subscriptionTier}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm font-medium text-slate-900">{customer.licenseCount}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate(`/customers/${customer.customerId}`)}
                            >
                              View
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/customers/${customer.customerId}`)}
                            >
                              Manage
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Improved Activity Feed with Timeline */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle size="lg">Recent Activity</CardTitle>
                  <CardDescription>Latest events across all customers</CardDescription>
                </div>
              </div>
              
              {/* Event type filter pills */}
              <div className="flex flex-wrap items-center gap-2">
                {EVENT_TYPES.map((type) => (
                  <button
                    key={type}
                    onClick={() => setEventTypeFilter(type)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                      eventTypeFilter === type
                        ? 'bg-orange-100 text-orange-700 border border-orange-200'
                        : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                    }`}
                  >
                    {type}
                    {type !== 'All' && filteredActivity.length > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-white text-[10px]">
                        {filteredActivity.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredActivity && filteredActivity.length > 0 ? (
              <>
                <div className="relative">
                  {/* Vertical timeline line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                  
                  {/* Activity items */}
                  <div className="space-y-4">
                    {filteredActivity.slice(0, activityLimit).map((activity, index) => {
                      const isSuccess = activity.eventStatus === 'Success';
                      const isError = activity.eventStatus === 'Error';
                      const isPending = activity.eventStatus === 'Pending';

                      return (
                        <div key={activity.id || index} className="relative flex gap-4 pl-12">
                          {/* Event node */}
                          <div className="absolute left-0 top-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${
                              isSuccess ? 'bg-emerald-500' : 
                              isError ? 'bg-rose-500' : 
                              isPending ? 'bg-amber-500' : 'bg-blue-500'
                            }`}>
                              {isSuccess ? (
                                <CheckCircle2 className="h-4 w-4 text-white" />
                              ) : isError ? (
                                <XCircle className="h-4 w-4 text-white" />
                              ) : isPending ? (
                                <AlertCircle className="h-4 w-4 text-white" />
                              ) : (
                                <Activity className="h-4 w-4 text-white" />
                              )}
                            </div>
                          </div>

                          {/* Event content */}
                          <div className="flex-1 p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition-all">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-900">
                                  {activity.eventType}
                                </p>
                                {activity.details && (
                                  <p className="text-xs text-slate-600 mt-1">
                                    {activity.details}
                                  </p>
                                )}
                              </div>
                              <StatusBadge 
                                status={
                                  isSuccess ? 'active' : 
                                  isError ? 'error' : 
                                  isPending ? 'pending' : 'offline'
                                }
                                label={activity.eventStatus}
                              />
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTimeAgo(activity.timestamp)}
                              </span>
                              <span>•</span>
                              <span className="flex items-center gap-1" title={activity.customerId}>
                                <Building2 className="h-3 w-3" />
                                {customerNameById.get(activity.customerId) ?? activity.customerId}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Load More button */}
                {filteredActivity.length > activityLimit && (
                  <div className="mt-6 text-center">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setActivityLimit(prev => prev + 10)}
                    >
                      Load More Activities
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Activity className="mx-auto h-12 w-12 text-slate-300" />
                <h3 className="mt-4 text-sm font-medium text-slate-900">
                  {eventTypeFilter === 'All' ? 'No recent activity' : `No ${eventTypeFilter.toLowerCase()} activity`}
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Activity will appear here as customers use your services.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
}
